import { currencyCodeSuffix } from '../../utils/money';
import { useBookingLocale } from '../../hooks/useBookingLocale';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { BookingStatus } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  DD,
  DescriptionList,
  DT,
  Field,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Strong,
  Textarea,
} from '../../components/ui';
import {
  useCancelJobMutation,
  useJobCancellationPreviewQuery,
  useServiceJobAsCustomerQuery,
  useUpdateJobAccessMutation,
  useWithdrawJobMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import { formatMoney } from '../ServiceProviderPage/priceTables';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import {
  canCancel,
  canFindAnother,
  canWithdraw,
  countdownText,
  jobItemLabels,
  JOBS_PATH,
  jobStatusView,
  rebookSearch,
  windowText,
  type ServiceJob,
} from './serviceJob';
import { CustomerJobSections } from './CustomerJobSections';
import { searchPath, workerPath } from './serviceSearchState';
import { DateTime } from 'luxon';

const Columns = styled.div`
  ${tw`grid grid-cols-1 gap-6 lg:grid-cols-3`}
`;

const Main = styled.div`
  ${tw`flex flex-col gap-6 lg:col-span-2 min-w-0`}
`;

const Side = styled.div`
  ${tw`flex flex-col gap-6 min-w-0`}
`;

const Thumb = styled.img`
  ${tw`object-cover`}
  width: 64px;
  height: 64px;
  border-radius: var(--radius-md);
  background: var(--color-bg-secondary);
`;

const Line = styled.div<{ $strong?: boolean }>`
  ${tw`flex justify-between gap-4 text-sm`}
  font-weight: ${({ $strong }) => ($strong ? 600 : 400)};
  color: var(--color-text-primary);
`;

function CancelJobModal({ job, onClose }: { job: ServiceJob; onClose: () => void }) {
  const jobLocale = useBookingLocale(job.id, 'guest');
  const preview = useJobCancellationPreviewQuery({
    variables: { bookingId: job.id },
    fetchPolicy: 'network-only',
  });
  const [cancelJob, { loading }] = useCancelJobMutation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const p = preview.data?.jobCancellationPreview;

  const confirm = async () => {
    setError(null);
    try {
      await cancelJob({ variables: { bookingId: job.id, reason: reason.trim() || null } });
      toast.success('Job cancelled');
      onClose();
    } catch (e) {
      setError(stayErrorMessage(e, 'Could not cancel. Try again.'));
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Cancel this job">
      <Stack $gap={4}>
        <Strong>Cancel this job?</Strong>
        {preview.loading && !p ? (
          <LoadingBlock label="Checking the fee" />
        ) : preview.error ? (
          <Alert tone="error" title="Cannot cancel">
            {stayErrorMessage(preview.error)}
          </Alert>
        ) : p ? (
          <Stack $gap={4} data-testid="job-cancel-preview">
            <Muted>{p.message}</Muted>
            <Line>
              <span>Cancellation fee</span>
              <span>{formatMoney(p.fee)}</span>
            </Line>
            <Line $strong>
              <span>Back to you</span>
              <span>
                {formatMoney(p.refund)}
                {currencyCodeSuffix(jobLocale.currency)}
              </span>
            </Line>
          </Stack>
        ) : null}
        <Field
          label="Why are you cancelling?"
          htmlFor="cancel-reason"
          hint="Optional. The Provider sees this."
        >
          <Textarea
            id="cancel-reason"
            rows={2}
            maxLength={500}
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </Field>
        {error && (
          <Alert tone="error" title="Not cancelled">
            {error}
          </Alert>
        )}
        <InlineRow>
          <Button
            type="button"
            theme="danger"
            text="Cancel job"
            onClick={confirm}
            isLoading={loading}
            isDisabled={!p}
            dataTestId="job-cancel-confirm"
          />
          <Button type="button" theme="text" text="Keep it" onClick={onClose} />
        </InlineRow>
      </Stack>
    </Modal>
  );
}

/**
 * A job as its customer sees it (spec 019 W16, first version): status and
 * countdown, the worker, the window, what was booked and the price with the
 * hold, entry details for this job, and withdraw, cancel (with the server's
 * fee preview) or find another worker after a request ended. Once booked,
 * `CustomerJobSections` adds the timeline and everything after the work (W21).
 */
export default function ServiceJobDetailPage() {
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  // Spec 028: the job's currency (CAD totals carry the code, D14) and tax label.
  const jobLocale = useBookingLocale(bookingId, 'guest');
  const navigate = useNavigate();
  const query = useServiceJobAsCustomerQuery({
    variables: { id: bookingId },
    fetchPolicy: 'cache-and-network',
  });
  const [withdrawJob, { loading: withdrawing }] = useWithdrawJobMutation();
  const [updateAccess, { loading: savingAccess }] = useUpdateJobAccessMutation();
  const [cancelling, setCancelling] = useState(false);
  const [access, setAccess] = useState('');
  const [editingAccess, setEditingAccess] = useState(false);
  const job = query.data?.getBookingAsGuest ?? null;

  if (query.loading && !job) return <LoadingBlock label="Loading the job" />;
  if (!job || !job.serviceJob) {
    return (
      <PageContainer width="md">
        <Alert tone="error" title="Job not found">
          {query.error ? stayErrorMessage(query.error) : 'This job could not be loaded.'}{' '}
          <Link to={JOBS_PATH}>Your jobs</Link>
        </Alert>
      </PageContainer>
    );
  }

  const info = job.serviceJob;
  const status = jobStatusView(job);
  const worker = job.car?.serviceWorker ?? null;
  const photo =
    worker?.equipmentPhotos[0] ?? job.car?.images?.[0]?.imageUrl ?? job.car?.thumbnailUrl;
  const receipt = job.receipt;
  const addOns = Array.isArray(info.selectedAddOns) ? (info.selectedAddOns as any[]) : [];
  const open = job.status === BookingStatus.Booked || job.status === BookingStatus.Requested;

  const countdown =
    job.status === BookingStatus.Requested && info.respondBy
      ? `The worker answers ${countdownText(Date.parse(info.respondBy))}. You are not charged until then.`
      : job.status === BookingStatus.Booked && info.windowStartAt > Date.now()
        ? `Starts ${countdownText(info.windowStartAt)}.`
        : null;

  const onWithdraw = async () => {
    if (!window.confirm('Withdraw this request? The hold on your card is released.')) return;
    try {
      await withdrawJob({ variables: { bookingId: job.id } });
      toast.success('Request withdrawn');
    } catch (e) {
      toast.error(stayErrorMessage(e, 'Could not withdraw'));
    }
  };

  const onSaveAccess = async () => {
    try {
      await updateAccess({
        variables: { bookingId: job.id, accessOverride: access.trim() || null },
      });
      toast.success(
        access.trim()
          ? 'Entry details saved for this job'
          : 'This job uses your saved entry details',
      );
      setEditingAccess(false);
    } catch (e) {
      toast.error(stayErrorMessage(e, 'Could not save'));
    }
  };

  const money = (value?: number | null) => formatMoney(Number(value ?? 0));

  return (
    <PageContainer width="xl" canvas data-testid="service-job">
      <PageHeader
        eyebrow="Job"
        title={jobItemLabels(info.items).join(' + ') || 'Service job'}
        description={windowText(info.windowStartAt, info.windowEndAt, info.timezone)}
        backTo={{ to: JOBS_PATH, label: 'Your jobs' }}
        actions={
          <Badge tone={status.tone} dot data-testid="job-status">
            {status.label}
          </Badge>
        }
      />
      <Columns>
        <Main>
          {countdown && (
            <Alert
              tone="info"
              title={
                job.status === BookingStatus.Requested ? 'Waiting for the worker' : 'Coming up'
              }
            >
              {countdown}
            </Alert>
          )}
          {(job.status === BookingStatus.Pending || job.status === BookingStatus.CardDeclined) &&
            worker && (
              <Alert tone="warning" title="Payment not finished">
                <Link to={`/services/book/${worker.carId}/${job.id}`}>Finish paying</Link> to keep
                this time.
              </Alert>
            )}
          {canFindAnother(job) && (
            <Alert
              tone="info"
              title={
                job.status === BookingStatus.DeclinedByHost
                  ? 'The worker could not take it'
                  : 'The worker did not answer in time'
              }
              actions={
                <Button
                  type="button"
                  size="sm"
                  text="Find another worker"
                  onClick={() => navigate(searchPath(rebookSearch(job)))}
                  dataTestId="job-find-another"
                />
              }
            >
              You were not charged. Search again with the same date and window.
            </Alert>
          )}
          {(job.status === BookingStatus.Booked || job.status === BookingStatus.Completed) && (
            <CustomerJobSections job={job} />
          )}
          <Card>
            <CardHeader title="Where and when" />
            <CardBody>
              <DescriptionList>
                <DT>Window</DT>
                <DD>{windowText(info.windowStartAt, info.windowEndAt, info.timezone)}</DD>
                {info.site && (
                  <>
                    <DT>{info.site.nickname || 'Place'}</DT>
                    <DD>
                      {[
                        info.site.addressLine1,
                        info.site.addressLine2,
                        `${info.site.city} ${info.site.zip}`,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </DD>
                  </>
                )}
                <DT>Services</DT>
                <DD>{jobItemLabels(info.items).join(', ')}</DD>
                {addOns.length > 0 && (
                  <>
                    <DT>Add-ons</DT>
                    <DD>
                      {addOns
                        .map(a => `${a.label}${a.quantity > 1 ? ` × ${a.quantity}` : ''}`)
                        .join(', ')}
                    </DD>
                  </>
                )}
                {info.maxBillableMinutes ? (
                  <>
                    <DT>Handover</DT>
                    <DD>
                      Billed for the time it takes, up to {Math.round(info.maxBillableMinutes / 60)}{' '}
                      hours
                    </DD>
                  </>
                ) : null}
                {info.customerNote && (
                  <>
                    <DT>Your note</DT>
                    <DD>{info.customerNote}</DD>
                  </>
                )}
              </DescriptionList>
            </CardBody>
          </Card>
          {open && (
            <Card>
              <CardHeader
                title="Entry for this job"
                description="Workers see entry details 2 hours before the window. Leave empty to use the saved details of the place."
                actions={
                  !editingAccess ? (
                    <Button
                      type="button"
                      theme="secondary"
                      size="sm"
                      text="Change"
                      onClick={() => setEditingAccess(true)}
                    />
                  ) : undefined
                }
              />
              {editingAccess && (
                <CardBody>
                  <Stack $gap={4}>
                    <Textarea
                      aria-label="Entry details for this job"
                      rows={3}
                      maxLength={2000}
                      value={access}
                      onChange={e => setAccess(e.target.value)}
                      data-testid="job-access"
                    />
                    <InlineRow>
                      <Button
                        type="button"
                        text="Save"
                        onClick={onSaveAccess}
                        isLoading={savingAccess}
                      />
                      <Button
                        type="button"
                        theme="text"
                        text="Cancel"
                        onClick={() => setEditingAccess(false)}
                      />
                    </InlineRow>
                  </Stack>
                </CardBody>
              )}
            </Card>
          )}
          <Card>
            <CardHeader title="Price" />
            <CardBody>
              <Stack $gap={4} data-testid="job-receipt">
                <Line>
                  <span>Services</span>
                  <span>{money(receipt.serviceSubtotal)}</span>
                </Line>
                {receipt.addOnsSubtotal ? (
                  <Line>
                    <span>Add-ons</span>
                    <span>{money(receipt.addOnsSubtotal)}</span>
                  </Line>
                ) : null}
                {receipt.travelFee ? (
                  <Line>
                    <span>Travel</span>
                    <span>{money(receipt.travelFee)}</span>
                  </Line>
                ) : null}
                <Line>
                  <span>Service fee</span>
                  <span>{money(receipt.platformServiceFee)}</span>
                </Line>
                {receipt.serviceTax ? (
                  <Line>
                    <span>{jobLocale.taxLabel}</span>
                    <span>{money(receipt.serviceTax)}</span>
                  </Line>
                ) : null}
                <Line $strong>
                  <span>{receipt.finalTotal != null ? 'Charged' : 'Total'}</span>
                  <span>
                    {money(receipt.finalTotal ?? receipt.totalPrice)}
                    {currencyCodeSuffix(jobLocale.currency)}
                  </span>
                </Line>
                {receipt.authorizedAmount != null && receipt.finalTotal == null && (
                  <Muted $size="sm">
                    {info.authorizeAfter && Date.parse(info.authorizeAfter) > Date.now()
                      ? `We hold ${money(receipt.authorizedAmount)} on your card on ${DateTime.fromISO(info.authorizeAfter).toLocaleString(DateTime.DATE_MED)}.`
                      : `${money(receipt.authorizedAmount)} is held on your card.`}{' '}
                    {receipt.addOnAllowance
                      ? `Up to ${money(receipt.addOnAllowance)} of it covers add-ons you approve on site; the rest is released after the job.`
                      : 'You are charged after the job.'}
                  </Muted>
                )}
              </Stack>
            </CardBody>
          </Card>
        </Main>
        <Side>
          {worker && (
            <Card>
              <CardBody>
                <Stack $gap={4}>
                  <InlineRow>
                    {photo && <Thumb src={toPublicS3ObjectUrl(photo)} alt="" />}
                    <div>
                      <Strong>{worker.displayName}</Strong>
                      <div>
                        <Muted $size="sm">
                          {worker.provider.businessName} · {worker.city} {worker.zip}
                        </Muted>
                      </div>
                    </div>
                  </InlineRow>
                  <InlineRow style={{ flexWrap: 'wrap' }}>
                    {worker.provider.insuranceVerified && (
                      <Badge tone="success" size="sm">
                        Insured
                      </Badge>
                    )}
                    {worker.backgroundCheckVerified && (
                      <Badge tone="success" size="sm">
                        Background check
                      </Badge>
                    )}
                  </InlineRow>
                  <Link to={workerPath(worker.carId)}>See the worker</Link>
                </Stack>
              </CardBody>
            </Card>
          )}
          {(canWithdraw(job) || canCancel(job)) && (
            <Card>
              <CardBody>
                <Stack $gap={4}>
                  {canWithdraw(job) && (
                    <Button
                      type="button"
                      theme="secondary"
                      text="Withdraw request"
                      onClick={onWithdraw}
                      isLoading={withdrawing}
                      dataTestId="job-withdraw"
                    />
                  )}
                  {canCancel(job) && (
                    <Button
                      type="button"
                      theme="secondary"
                      text="Cancel job"
                      onClick={() => setCancelling(true)}
                      dataTestId="job-cancel"
                    />
                  )}
                  <Muted $size="sm">
                    Free until 24 hours before the window. After that, a fee applies.
                  </Muted>
                </Stack>
              </CardBody>
            </Card>
          )}
        </Side>
      </Columns>
      {cancelling && <CancelJobModal job={job} onClose={() => setCancelling(false)} />}
    </PageContainer>
  );
}
